import { useState, useMemo } from 'react';
import {
  Search,
  Sparkles,
  ShoppingCart,
  TrendingUp,
  Clock,
  Package,
  X,
} from 'lucide-react';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils/formatters';

import {
  mockItems,
  mockInventory,
  getSuppliersForItem,
  type Item,
  type Supplier,
  type SupplierQuote,
} from '../data/mockData';

interface EnrichedSupplier {
  supplier: Supplier;
  quote: SupplierQuote | undefined;
  rating: number;
  inStock: boolean;
}

interface EnrichedProduct extends Item {
  suppliers_enriched: EnrichedSupplier[];
  avgPrice: number;
  lowestPrice: number;
  currentInventory: number;
  reorderPoint: number;
  priceHistory: string;
  demandForecast: string;
}

const categories = [
  { id: 'all', name: 'All Products' },
  { id: 'raw-material', name: 'Raw Materials' },
  { id: 'component', name: 'Components' },
  { id: 'packaging', name: 'Packaging' },
];

function getCategoryLabel(category: Item['category']) {
  const labels = {
    'raw-material': 'Raw Material',
    component: 'Component',
    packaging: 'Packaging',
  };
  return labels[category];
}

export function ItemsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] =
    useState<EnrichedProduct | null>(null);

  // Enrich products with supplier and inventory data
  const enrichedProducts: EnrichedProduct[] = useMemo(() => {
    return mockItems.map((item) => {
      const suppliersData = getSuppliersForItem(item.id);
      const inventory = mockInventory.find((inv) => inv.itemId === item.id);

      const suppliers_enriched: EnrichedSupplier[] = suppliersData.map(
        ({ supplier, quote }) => ({
          supplier: supplier!,
          quote,
          rating: supplier!.qualityScore / 20, // Convert 0-100 to 0-5 scale
          inStock: supplier!.status === 'active',
        }),
      );

      const prices = suppliers_enriched
        .map((s) => s.quote?.pricePerUnit)
        .filter((p): p is number => p !== undefined);

      const avgPrice =
        prices.length > 0
          ? prices.reduce((a, b) => a + b, 0) / prices.length
          : item.unitCost;
      const lowestPrice =
        prices.length > 0 ? Math.min(...prices) : item.unitCost;

      // Generate dynamic price/demand info based on item data
      const priceHistories = [
        'Decreased 2% this month',
        'Decreased 5% this month',
        'Stable pricing',
        'Increased 3% this month',
      ];
      const demandForecasts = [
        'High demand expected',
        'Stable demand',
        'Increasing demand',
        'Seasonal uptick expected',
      ];

      return {
        ...item,
        suppliers_enriched,
        avgPrice,
        lowestPrice,
        currentInventory: inventory?.availableStock ?? 0,
        reorderPoint: inventory?.reorderPoint ?? 0,
        priceHistory:
          priceHistories[
            item.id.charCodeAt(item.id.length - 1) % priceHistories.length
          ],
        demandForecast:
          demandForecasts[
            item.id.charCodeAt(item.id.length - 1) % demandForecasts.length
          ],
      };
    });
  }, []);

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    return enrichedProducts.filter((p) => {
      const matchesSearch =
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === 'all' || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [enrichedProducts, searchQuery, selectedCategory]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: enrichedProducts.length };
    for (const product of enrichedProducts) {
      counts[product.category] = (counts[product.category] || 0) + 1;
    }
    return counts;
  }, [enrichedProducts]);

  // Stats calculations
  const stats = useMemo(() => {
    const totalSuppliers = filteredProducts.reduce(
      (sum, p) => sum + p.suppliers_enriched.length,
      0,
    );
    const avgSuppliersPerProduct =
      filteredProducts.length > 0
        ? (totalSuppliers / filteredProducts.length).toFixed(1)
        : '0';

    const leadTimes = filteredProducts.map((p) => p.leadTimeWeeks);
    const avgLeadTime =
      leadTimes.length > 0
        ? Math.round(leadTimes.reduce((a, b) => a + b, 0) / leadTimes.length)
        : 0;

    const inStockCount = filteredProducts.filter(
      (p) => p.currentInventory > 0,
    ).length;
    const inStockPercentage =
      filteredProducts.length > 0
        ? Math.round((inStockCount / filteredProducts.length) * 100)
        : 0;

    return {
      totalProducts: filteredProducts.length,
      avgSuppliersPerProduct,
      avgLeadTime,
      inStockPercentage,
    };
  }, [filteredProducts]);

  // Get best supplier based on rating, price, and lead time
  const getBestSupplier = (suppliers: EnrichedSupplier[]) => {
    if (suppliers.length === 0) return null;
    return suppliers.reduce((best, current) => {
      const bestPrice = best.quote?.pricePerUnit ?? Infinity;
      const currentPrice = current.quote?.pricePerUnit ?? Infinity;
      const bestLeadTime = best.quote?.leadTimeWeeks ?? Infinity;
      const currentLeadTime = current.quote?.leadTimeWeeks ?? Infinity;

      const bestScore = best.rating * 20 - bestPrice / 10 - bestLeadTime * 2;
      const currentScore =
        current.rating * 20 - currentPrice / 10 - currentLeadTime * 2;
      return currentScore > bestScore ? current : best;
    }, suppliers[0]);
  };

  return (
    <Page
      title="Supplier Catalog"
      subtitle="Browse products and compare supplier pricing"
    >
      <div className="flex flex-col gap-6">
        {/* Search Bar and Action */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by product name, SKU, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button className="gap-2">
            <Sparkles className="w-4 h-4" />
            Find Best Price
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Sidebar - Categories */}
          <div className="w-56 flex-shrink-0">
            <Card className="p-4">
              <h3 className="font-semibold text-foreground mb-3">Categories</h3>
              <div className="space-y-1">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{category.name}</span>
                      <span className="text-xs opacity-70">
                        {categoryCounts[category.id] ?? 0}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="text-sm text-muted-foreground mb-1">
                  Total Products
                </div>
                <div className="text-2xl font-semibold">
                  {stats.totalProducts}
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-muted-foreground mb-1">
                  Avg Suppliers/Product
                </div>
                <div className="text-2xl font-semibold">
                  {stats.avgSuppliersPerProduct}
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-muted-foreground mb-1">
                  Avg Lead Time
                </div>
                <div className="text-2xl font-semibold">
                  {stats.avgLeadTime} weeks
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-muted-foreground mb-1">
                  In Stock Items
                </div>
                <div className="text-2xl font-semibold text-green-600">
                  {stats.inStockPercentage}%
                </div>
              </Card>
            </div>

            {/* Products Table */}
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Best Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Suppliers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Lead Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Stock Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredProducts.map((product) => {
                      const bestSupplier = getBestSupplier(
                        product.suppliers_enriched,
                      );
                      const stockPercentage =
                        product.reorderPoint > 0
                          ? (product.currentInventory / product.reorderPoint) *
                            100
                          : 100;
                      const isLowStock = stockPercentage < 80;

                      return (
                        <tr
                          key={product.id}
                          className="hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => setSelectedProduct(product)}
                        >
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium">
                                {product.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                SKU: {product.sku}
                              </div>
                              <Badge
                                variant="secondary"
                                className="mt-1 text-xs"
                              >
                                {getCategoryLabel(product.category)}
                              </Badge>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold">
                              {formatCurrency(product.lowestPrice, {
                                locale: 'en-US',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              <span className="font-normal text-muted-foreground">
                                /{product.unitOfMeasure}
                              </span>
                            </div>
                            {bestSupplier && (
                              <div className="text-xs text-muted-foreground">
                                {bestSupplier.supplier.name}
                              </div>
                            )}
                            {product.priceHistory.includes('Decreased') && (
                              <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                <TrendingUp className="w-3 h-3 rotate-180" />
                                {product.priceHistory}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              {product.suppliers_enriched.length} suppliers
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Avg:{' '}
                              {formatCurrency(product.avgPrice, {
                                locale: 'en-US',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              /{product.unitOfMeasure}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5 text-sm">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              {bestSupplier?.quote?.leadTimeWeeks ??
                                product.leadTimeWeeks}{' '}
                              weeks
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div
                              className={`text-sm font-medium ${isLowStock ? 'text-red-600' : 'text-green-600'}`}
                            >
                              {product.currentInventory.toLocaleString()}{' '}
                              {product.unitOfMeasure}s
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {isLowStock ? 'Low stock' : 'In stock'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 h-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProduct(product);
                              }}
                            >
                              Compare
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredProducts.length === 0 && (
                <div className="p-12 text-center">
                  <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    No products found matching your search.
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-background rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold mb-2">
                    {selectedProduct.name}
                  </h2>
                  <div className="text-sm text-muted-foreground mb-2">
                    SKU: {selectedProduct.sku}
                  </div>
                  <div className="text-sm text-muted-foreground mb-3">
                    {selectedProduct.description}
                  </div>
                  <Badge variant="secondary">
                    {getCategoryLabel(selectedProduct.category)}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedProduct(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Product Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Current Inventory
                  </div>
                  <div className="text-2xl font-semibold">
                    {selectedProduct.currentInventory.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Reorder at {selectedProduct.reorderPoint.toLocaleString()}
                  </div>
                </div>
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Average Price
                  </div>
                  <div className="text-2xl font-semibold">
                    {formatCurrency(selectedProduct.avgPrice, {
                      locale: 'en-US',
                      currency: 'USD',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    per {selectedProduct.unitOfMeasure}
                  </div>
                </div>
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Price Trend
                  </div>
                  <div
                    className={`text-sm font-semibold ${
                      selectedProduct.priceHistory.includes('Decreased')
                        ? 'text-green-600'
                        : ''
                    }`}
                  >
                    {selectedProduct.priceHistory}
                  </div>
                </div>
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Demand Forecast
                  </div>
                  <div className="text-sm font-semibold">
                    {selectedProduct.demandForecast}
                  </div>
                </div>
              </div>

              {/* AI Recommendation */}
              {(() => {
                const bestSupplier = getBestSupplier(
                  selectedProduct.suppliers_enriched,
                );
                if (!bestSupplier) return null;

                return (
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">
                          AI Recommendation
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Based on pricing, lead time, and supplier reliability,
                          we recommend{' '}
                          <span className="font-semibold text-foreground">
                            {bestSupplier.supplier.name}
                          </span>{' '}
                          for this product. They offer competitive pricing at{' '}
                          {formatCurrency(
                            bestSupplier.quote?.pricePerUnit ??
                              selectedProduct.unitCost,
                            {
                              locale: 'en-US',
                              currency: 'USD',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                          /{selectedProduct.unitOfMeasure} with a{' '}
                          {bestSupplier.quote?.leadTimeWeeks ??
                            selectedProduct.leadTimeWeeks}{' '}
                          week lead time and a {bestSupplier.rating.toFixed(1)}
                          /5 quality rating.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Suppliers Comparison */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Supplier Comparison
                </h3>
                <div className="space-y-3">
                  {selectedProduct.suppliers_enriched.map(
                    (enrichedSupplier, idx) => {
                      const bestSupplier = getBestSupplier(
                        selectedProduct.suppliers_enriched,
                      );
                      const isBest =
                        bestSupplier?.supplier.id ===
                        enrichedSupplier.supplier.id;
                      const price =
                        enrichedSupplier.quote?.pricePerUnit ??
                        selectedProduct.unitCost;
                      const moq =
                        enrichedSupplier.quote?.minimumQuantity ??
                        selectedProduct.minimumOrderQuantity;
                      const leadTimeWeeks =
                        enrichedSupplier.quote?.leadTimeWeeks ??
                        selectedProduct.leadTimeWeeks;

                      return (
                        <div
                          key={idx}
                          className={`border rounded-lg p-4 ${
                            isBest
                              ? 'border-primary bg-primary/5'
                              : 'border-border bg-card'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <h4 className="font-semibold">
                                {enrichedSupplier.supplier.name}
                              </h4>
                              {isBest && (
                                <Badge className="bg-primary text-primary-foreground">
                                  Best Value
                                </Badge>
                              )}
                              {!enrichedSupplier.inStock && (
                                <Badge variant="destructive">Inactive</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <span className="text-muted-foreground">
                                Quality:
                              </span>
                              <span className="font-semibold">
                                {enrichedSupplier.rating.toFixed(1)}/5
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-4 gap-4">
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">
                                Price
                              </div>
                              <div className="text-lg font-semibold">
                                {formatCurrency(price, {
                                  locale: 'en-US',
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                                /{selectedProduct.unitOfMeasure}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">
                                Lead Time
                              </div>
                              <div className="text-lg font-semibold">
                                {leadTimeWeeks} weeks
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">
                                Min Order Qty
                              </div>
                              <div className="text-lg font-semibold">
                                {moq.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">
                                Total Cost (MOQ)
                              </div>
                              <div className="text-lg font-semibold">
                                {formatCurrency(price * moq, {
                                  locale: 'en-US',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t flex items-center justify-between">
                            <div className="text-xs text-muted-foreground">
                              {enrichedSupplier.supplier.location}
                            </div>
                            <Button size="sm" className="gap-2">
                              <ShoppingCart className="w-4 h-4" />
                              Add to Cart
                            </Button>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
